import axios from 'axios';
import { getSdkOrigin } from '../../target/generated/sdk';
import { getRequestIdFromHeaders, parsePathname, resolveOrigin, resolveUrlTemplate } from './requestUrl.utilities';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { GraphqlOperation } from '../graphql/executeGraphQL';

/**
 * What an observer is told when a request starts.
 *
 * The query string and fragment are stripped before an observer sees anything, so nothing carried in them leaks out.
 * Use `urlTemplate` for anything that becomes a metric label.
 */
export type RequestLifecycleStart = {
  /** Upper-cased HTTP method. */
  method: string;
  /**
   * Request pathname, query string and fragment removed. It still contains ids, so it is for correlation and debugging,
   * not for labels.
   */
  path: string;
  /** Declared template for `path`, or the constant `{untemplated}`. Never derived from `path`. */
  urlTemplate: string;
  /** Seeq request id header, when one was set. */
  requestId?: string;
  /** The GraphQL operation the request executes, when the caller declared one on the request config. */
  graphqlOperation?: GraphqlOperation;
  /** `performance.now()` when the request started. */
  startedAt: number;
};

export type RequestLifecycleOutcome = 'response' | 'network-error' | 'cancelled';

/** What an observer is told once a request settles. */
export type RequestLifecycleEnd = RequestLifecycleStart & {
  /** Milliseconds from `startedAt` until the request settled. */
  durationMs: number;
  /** How the request settled. */
  outcome: RequestLifecycleOutcome;
  /** Set whenever a response was received, success or error. */
  statusCode?: number;
  /** Axios error code (`ERR_NETWORK`, `ECONNABORTED`, and so on) when no response was received. */
  errorCode?: string;
};

/**
 * Two-phase observer. Whatever `onRequestStart` returns is handed back to `onRequestEnd`, so an observer can keep
 * per-request context such as the page the request started on.
 *
 * Written as properties rather than methods so TypeScript checks these signatures strictly.
 */
export type RequestLifecycleObserver<TContext = unknown> = {
  onRequestStart: (start: RequestLifecycleStart) => TContext;
  onRequestEnd: (end: RequestLifecycleEnd, context: TContext) => void;
};

// Only observers whose `onRequestStart` returned normally are kept, so `onRequestEnd` always gets back a context that
// observer really made.
type StartedObserver = { observer: RequestLifecycleObserver; context: unknown };
type InFlight = { start: RequestLifecycleStart; started: StartedObserver[] };

const observers = new Set<RequestLifecycleObserver>();
// Keyed by the config object axios threads from request interceptors to response.config.
const inFlight = new WeakMap<object, InFlight>();
const warnedObservers = new WeakSet<RequestLifecycleObserver>();

/**
 * Registers an observer for every instrumented request. Returns the unsubscribe function. A request copies the observer
 * list when it starts, so unsubscribing mid-request still delivers that request's end, and subscribing mid-request
 * delivers neither half. Some failures arrive with no config to match on, so a start can occasionally never get an end
 * — don't leak if you allocate on start.
 */
export function subscribeToRequestLifecycle<TContext>(observer: RequestLifecycleObserver<TContext>): () => void {
  observers.add(observer as RequestLifecycleObserver);
  return () => {
    observers.delete(observer as RequestLifecycleObserver);
  };
}

/** Test-only: forget every observer. */
export function resetRequestLifecycleForTesting(): void {
  observers.clear();
}

/**
 * Runs an observer callback and swallows what it throws. `ok` tells a throw apart from a legitimate `undefined`. Each
 * observer is warned about once.
 */
function guarded<T>(
  observer: RequestLifecycleObserver,
  phase: string,
  fn: () => T,
): { ok: true; value: T } | { ok: false } {
  try {
    return { ok: true, value: fn() };
  } catch (error) {
    if (!warnedObservers.has(observer)) {
      warnedObservers.add(observer);
      console.warn(
        `Request lifecycle observer threw during ${phase}. The error is ignored and further ` +
          `errors from this observer are not logged.`,
        error,
      );
    }
    return { ok: false };
  }
}

function buildStart(config: InternalAxiosRequestConfig, path: string): RequestLifecycleStart {
  return {
    method: (config.method ?? 'get').toUpperCase(),
    path,
    urlTemplate: resolveUrlTemplate(config.urlTemplate),
    requestId: getRequestIdFromHeaders(config.headers),
    graphqlOperation: config.graphqlOperation,
    startedAt: performance.now(),
  };
}

// Params are nulled so no serializer runs here and no query text is built; the adapter serializes once.
function requestUri(instance: AxiosInstance, config: AxiosRequestConfig): string | undefined {
  try {
    return instance.getUri({ ...config, params: null }) ?? '';
  } catch {
    return undefined;
  }
}

function onRequest(instance: AxiosInstance, config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (observers.size === 0) {
    return config;
  }
  const origin = resolveOrigin(instance.defaults.baseURL) ?? getSdkOrigin();
  const path = parsePathname(requestUri(instance, config), origin);
  if (path === undefined) {
    return config;
  }
  const start = buildStart(config, path);
  const started: StartedObserver[] = [];
  observers.forEach((observer) => {
    const result = guarded(observer, 'onRequestStart', () => observer.onRequestStart(start));
    if (result.ok) {
      started.push({ observer, context: result.value });
    }
  });
  inFlight.set(config, { start, started });
  return config;
}

function finish(
  config: object | undefined,
  settle: Pick<RequestLifecycleEnd, 'outcome' | 'statusCode' | 'errorCode'>,
): void {
  if (!config) {
    // Nothing to match on. Happens when a non-axios error is thrown from an interceptor or a transform; if that request
    // had already started, it never gets an end.
    return;
  }
  const entry = inFlight.get(config);
  if (!entry) {
    return;
  }
  inFlight.delete(config);
  const end: RequestLifecycleEnd = {
    ...entry.start,
    durationMs: performance.now() - entry.start.startedAt,
    outcome: settle.outcome,
    statusCode: settle.statusCode,
    errorCode: settle.errorCode,
  };
  entry.started.forEach(({ observer, context }) => {
    guarded(observer, 'onRequestEnd', () => observer.onRequestEnd(end, context));
  });
}

function onResponseError(error: unknown): Promise<never> {
  if (axios.isAxiosError(error)) {
    const { config } = error;
    if (axios.isCancel(error)) {
      finish(config, { outcome: 'cancelled' });
    } else if (error.response) {
      finish(config, { outcome: 'response', statusCode: error.response.status });
    } else {
      finish(config, { outcome: 'network-error', errorCode: error.code });
    }
  }
  return Promise.reject(error);
}

/**
 * Attaches the lifecycle interceptors. Register them before any other interceptor on the instance: axios runs request
 * interceptors in reverse registration order and response interceptors in registration order, so registering first puts
 * both halves closest to the network.
 *
 * Relative request urls are always reported. Absolute ones only when they are on the instance's own origin, or failing
 * that the SDK's, so a call to an add-on tool on another host produces no events. A url axios cannot build is skipped
 * too. Either way the request itself is unaffected.
 */
export function attachLifecycleInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => onRequest(instance, config));
  instance.interceptors.response.use((response) => {
    finish(response.config, { outcome: 'response', statusCode: response.status });
    return response;
  }, onResponseError);
}
